import { useEffect, useMemo, useState } from 'react';
import { DownloadSelection, ExpansionPanel, EditableDenseTable, SnackbarField } from '@/components/common';
import { Checkbox, CircularProgress, IconButton, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useCompanyAnnualStore } from '@/store/audit/companyAnnualStore';
import type { BankConfirmation, CompanyAnnual } from '@/services/accountService/companyAnnualService';
import { auditAfsFromService } from '@/services/auditService/auditAfsFromService';
import type { BankConfBO } from '@/services/auditService/interface/afs-from';

type Props = {
  annualId: number;
  companyName?: string;
  periodTo?: string | null;
  periodFrom?: string | null;
  auditCertificateId?: number;
  onChange?: (patch: Partial<CompanyAnnual>) => void;
  disableAccordion?: boolean;
  hideDownload?: boolean;
  enableRowDownload?: boolean;
};

export default function EpBankConfirmations({ annualId, companyName, periodTo, periodFrom, auditCertificateId, onChange, disableAccordion = false, hideDownload = false, enableRowDownload = false }: Props) {
  const getBankConfirmations = useCompanyAnnualStore(s => s.getBankConfirmations);
  const [rows, setRows] = useState<BankConfirmation[]>([]);
  const [downloading, setDownloading] = useState(false);
  const [downloadingRowIndex, setDownloadingRowIndex] = useState<number | null>(null);
  const [downloadPromptOpen, setDownloadPromptOpen] = useState(false);
  const [downloadPromptRowIndex, setDownloadPromptRowIndex] = useState<number | null>(null);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedRowMap, setSelectedRowMap] = useState<Record<number, boolean>>({});
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState<'success' | 'error' | 'warning'>('success');

  useEffect(() => {
    let mounted = true;
    (async () => {
      const list = await getBankConfirmations(annualId);
      if (!mounted) return;
      setRows(list || []);
    })();
    return () => { mounted = false; };
  }, [annualId, getBankConfirmations]);

  useEffect(() => {
    if (rows.length <= 1 && multiSelectMode) {
      setMultiSelectMode(false);
      setSelectedRowMap({});
    }
  }, [multiSelectMode, rows.length]);

  const handleDownload = async (args?: { rowIndex?: number; bankConfirmationsOverride?: BankConfirmation[]; exitMultiSelectAfter?: boolean }) => {
    if (downloading) return;

    const certificateId = Number(auditCertificateId || 0) || 0;
    if (!certificateId) {
      setSnackSeverity('error');
      setSnackMessage('Audit Certificate is required.');
      setSnackOpen(true);
      return;
    }

    const effectiveCompanyName = String(companyName || '').trim();
    if (!effectiveCompanyName) {
      setSnackSeverity('error');
      setSnackMessage('Company Name is required.');
      setSnackOpen(true);
      return;
    }

    const effectivePeriodTo = String(periodTo || periodFrom || '').trim();
    if (!effectivePeriodTo) {
      setSnackSeverity('error');
      setSnackMessage('Period To is required.');
      setSnackOpen(true);
      return;
    }

    const sourceRows = args?.bankConfirmationsOverride ?? rows;
    const bankConfirmations = (sourceRows || []).map(r => ({
      ...r,
      bankName: String((r as any).bankName || ''),
      accountNumber: String((r as any).accountNumber || ''),
    }));

    if (!bankConfirmations.length) {
      setSnackSeverity('error');
      setSnackMessage('Please add at least one bank confirmation row.');
      setSnackOpen(true);
      return;
    }

    const invalidRowIndex = bankConfirmations.findIndex(r => {
      const bn = String(r.bankName || '').trim();
      const an = String(r.accountNumber || '').trim();
      return !bn || !an;
    });
    if (invalidRowIndex >= 0) {
      setSnackSeverity('error');
      setSnackMessage(`Row ${invalidRowIndex + 1}: Bank Name and Account Number are required.`);
      setSnackOpen(true);
      return;
    }

    const payload: BankConfBO = {
      companyName: effectiveCompanyName,
      periodTo: effectivePeriodTo,
      auditCertificateId: certificateId,
      bankConfirmations,
    };

    let downloadSucceeded = false;

    try {
      setDownloading(true);
      setDownloadingRowIndex(args?.rowIndex ?? null);
      const { blob, filename } = await auditAfsFromService.exportExcelBConfForm(payload);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'Bank_Confirmations.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      downloadSucceeded = true;

      setSnackSeverity('success');
      setSnackMessage('Bank confirmations exported.');
      setSnackOpen(true);
    } catch (e: any) {
      setSnackSeverity('error');
      setSnackMessage(e?.message || 'Failed to export bank confirmations.');
      setSnackOpen(true);
    } finally {
      setDownloading(false);
      setDownloadingRowIndex(null);

      if (args?.exitMultiSelectAfter && downloadSucceeded) {
        setMultiSelectMode(false);
        setSelectedRowMap({});
      }
    }
  };

  const selectedRowCount = useMemo(() => {
    return Object.values(selectedRowMap).filter(Boolean).length;
  }, [selectedRowMap]);

  const openDownloadPrompt = (rowIndex: number) => {
    if (downloading) return;
    setDownloadPromptRowIndex(rowIndex);
    setDownloadPromptOpen(true);
  };

  const handleRowDownloadClick = (rowIndex: number) => {
    const r = rows?.[rowIndex];
    if (!r) return;

    if ((rows?.length || 0) <= 1) {
      handleDownload({ rowIndex, bankConfirmationsOverride: [r] });
      return;
    }

    openDownloadPrompt(rowIndex);
  };

  const handlePromptSingleDownload = () => {
    const idx = downloadPromptRowIndex;
    const r = idx != null ? rows?.[idx] : undefined;
    setDownloadPromptOpen(false);
    setDownloadPromptRowIndex(null);
    if (!r || idx == null) return;
    handleDownload({ rowIndex: idx, bankConfirmationsOverride: [r] });
  };

  const handlePromptMultipleDownload = () => {
    const idx = downloadPromptRowIndex;
    setDownloadPromptOpen(false);
    setDownloadPromptRowIndex(null);
    setMultiSelectMode(true);

    const rowCount = rows?.length || 0;
    if (rowCount === 2) {
      setSelectedRowMap({ 0: true, 1: true });
      return;
    }

    if (idx == null || idx < 0 || idx >= rowCount) {
      setSelectedRowMap({});
      return;
    }
    setSelectedRowMap({ [idx]: true });
  };

  const headerActions = useMemo(() => {
    if (hideDownload) return null;
    if (!multiSelectMode) return null;

    return (
      <Tooltip title="Download">
        <span>
          <IconButton
            size="small"
            color="primary"
            onClick={() => {
              const selected = Object.entries(selectedRowMap)
                .filter(([, v]) => !!v)
                .map(([k]) => Number(k))
                .filter((i) => Number.isFinite(i) && i >= 0 && i < (rows?.length || 0));

              const bankConfirmationsOverride = selected.map(i => rows[i]).filter(Boolean) as BankConfirmation[];
              if (!bankConfirmationsOverride.length) return;
              handleDownload({ bankConfirmationsOverride, exitMultiSelectAfter: true });
            }}
            disabled={downloading || selectedRowCount <= 0}
          >
            {downloading && downloadingRowIndex == null ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    );
  }, [downloading, downloadingRowIndex, handleDownload, hideDownload, multiSelectMode, rows, selectedRowCount, selectedRowMap]);

  return (
    <ExpansionPanel
      title="Bank Confirmations (銀行詢證)"
      expanded={disableAccordion ? true : undefined}
      onChange={disableAccordion ? () => {} : undefined}
    >
      <EditableDenseTable
        head={["Bank Name", "Account Number", "Account Type"]}
        rows={(rows || []).map(b => [b.bankName || '', b.accountNumber || '', Number((b as any).accountType ?? 1)])}
        columnKeys={["bankName", "accountNumber", "accountType"]}
        defaultValuesByKey={{ accountType: 1 }}
        baseObjects={(rows || []).map(b => ({ id: (b as any).id }))}
        headerActions={headerActions}
        rowActions={(!hideDownload && enableRowDownload) ? ((rowIndex) => {
          if (multiSelectMode) {
            const checked = !!selectedRowMap[rowIndex];
            return (
              <Checkbox
                size="small"
                checked={checked}
                onChange={(e) => {
                  const nextChecked = e.target.checked;
                  setSelectedRowMap(prev => ({ ...prev, [rowIndex]: nextChecked }));
                }}
                disabled={downloading}
              />
            );
          }

          return (
            <Tooltip title="Download">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleRowDownloadClick(rowIndex)}
                  disabled={downloading}
                >
                  {downloadingRowIndex === rowIndex ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          );
        }) : undefined}
        columnConfigs={[
          { type: 'text' },
          { type: 'text' },
          {
            type: 'select',
            options: [
              { label: 'Overdraft', value: 0 },
              { label: 'Bank Balance', value: 1 },
            ],
          },
        ]}
        onRowsChangeObjects={(list) => {
          const next = (list as any[]).map((o) => ({
            id: Number(o.id ?? 0) || 0,
            bankName: String(o.bankName || ''),
            accountNumber: String(o.accountNumber || ''),
            isValid: 1, // Always set to Valid, only set to Invalid when delete
            accountType: Number(o.accountType ?? 1) || 0,
          })) as any as BankConfirmation[];
          setRows(next);
          onChange?.({ bankConfirmations: next } as Partial<CompanyAnnual>);
        }}
      />
      <DownloadSelection
        open={downloadPromptOpen}
        onClose={() => {
          setDownloadPromptOpen(false);
          setDownloadPromptRowIndex(null);
        }}
        onMultipleDownload={handlePromptMultipleDownload}
        onSingleDownload={handlePromptSingleDownload}
      />
      <SnackbarField
        open={snackOpen}
        message={snackMessage}
        severity={snackSeverity}
        onClose={() => setSnackOpen(false)}
      />
    </ExpansionPanel>
  );
}
